import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, VStack, HStack, Text, Heading, Badge, Button, Select,
  Divider, Container, Flex, Icon, Grid, GridItem, SimpleGrid,
  Modal, ModalOverlay, ModalContent, ModalBody, useDisclosure, useToast,
} from '@chakra-ui/react';
import {
  FaCreditCard, FaCheckCircle, FaFileInvoiceDollar, FaLock,
  FaVial, FaStethoscope, FaSyringe, FaTimes, FaArrowLeft,
  FaRegCreditCard, FaPlus, FaWallet,
} from 'react-icons/fa';

const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Mock invoice data keyed by mock ID
const MOCK_INVOICES = {
  '1': {
    invoice_number: 'INV-2024-0047',
    payment_status: 'unpaid',
    created_at: '2024-03-15T10:00:00Z',
    batch_number: 'BATCH-0047',
    lab_name: 'Sunrise Diagnostics',
    lab_email: 'billing@sunrisedx.com',
    total_amount: 125.00,
    line_items: [
      { patient_name: 'John Smith', patient_dob: '1985-06-12', accession: 'ACC-90821', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-14', tests: ['CBC', 'CMP', 'Lipid Panel'], diagnosis: 'Z00.00 - General adult examination', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 5.00 },
      { patient_name: 'Emily Davis', patient_dob: '1992-11-03', accession: 'ACC-90822', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-14', tests: ['TSH', 'Free T4'], diagnosis: 'E03.9 - Hypothyroidism', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Robert Johnson', patient_dob: '1978-02-28', accession: 'ACC-90823', doctor_name: 'Dr. James Lee', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-14', tests: ['HbA1c', 'Glucose'], diagnosis: 'E11.9 - Type 2 diabetes', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Lisa Wong', patient_dob: '1990-08-17', accession: 'ACC-90824', doctor_name: 'Dr. James Lee', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-15', tests: ['Vitamin D', 'B12', 'Iron Panel'], diagnosis: 'D50.9 - Iron deficiency anemia', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Michael Brown', patient_dob: '1965-04-22', accession: 'ACC-90825', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-15', tests: ['PSA', 'CBC'], diagnosis: 'Z12.5 - Prostate screening', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
    ],
  },
  '2': {
    invoice_number: 'INV-2024-0046',
    payment_status: 'unpaid',
    created_at: '2024-03-12T09:00:00Z',
    batch_number: 'BATCH-0046',
    lab_name: 'Sunrise Diagnostics',
    lab_email: 'billing@sunrisedx.com',
    total_amount: 75.00,
    line_items: [
      { patient_name: 'Sandra Miller', patient_dob: '1988-01-15', accession: 'ACC-90810', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-03-11', tests: ['CBC', 'BMP'], diagnosis: 'R53.83 - Fatigue', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 5.00 },
      { patient_name: 'David Chen', patient_dob: '1975-09-30', accession: 'ACC-90811', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-03-11', tests: ['Liver Panel', 'GGT'], diagnosis: 'K76.0 - Fatty liver', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Karen Taylor', patient_dob: '1970-12-05', accession: 'ACC-90812', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-03-12', tests: ['Thyroid Panel'], diagnosis: 'E05.90 - Thyrotoxicosis', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
    ],
  },
  '3': {
    invoice_number: 'INV-2024-0045',
    payment_status: 'unpaid',
    created_at: '2024-03-08T14:00:00Z',
    batch_number: 'BATCH-0045',
    lab_name: 'Sunrise Diagnostics',
    lab_email: 'billing@sunrisedx.com',
    total_amount: 200.00,
    line_items: [
      { patient_name: 'Patricia Anderson', patient_dob: '1982-07-19', accession: 'ACC-90790', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-07', tests: ['CBC', 'CMP', 'UA'], diagnosis: 'Z00.00 - General examination', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 5.00 },
      { patient_name: 'Thomas Wright', patient_dob: '1955-03-11', accession: 'ACC-90791', doctor_name: 'Dr. James Lee', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-07', tests: ['PT/INR', 'CBC'], diagnosis: 'I48.91 - Atrial fibrillation', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Jennifer Lopez', patient_dob: '1995-05-25', accession: 'ACC-90792', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-07', tests: ['hCG', 'CBC', 'Blood Type'], diagnosis: 'Z34.00 - Pregnancy supervision', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'William Harris', patient_dob: '1960-10-08', accession: 'ACC-90793', doctor_name: 'Dr. James Lee', phlebotomist_name: 'James Wilson', collection_date: '2024-03-08', tests: ['Renal Panel', 'CBC'], diagnosis: 'N18.3 - CKD stage 3', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Nancy Clark', patient_dob: '1987-01-30', accession: 'ACC-90794', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-03-08', tests: ['Celiac Panel', 'IgA'], diagnosis: 'K90.0 - Celiac disease', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Richard Martin', patient_dob: '1972-08-14', accession: 'ACC-90795', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-03-08', tests: ['Testosterone', 'SHBG', 'LH'], diagnosis: 'E29.1 - Testicular hypofunction', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Susan Robinson', patient_dob: '1968-04-02', accession: 'ACC-90796', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-08', tests: ['ANA', 'ESR', 'CRP'], diagnosis: 'M35.9 - Autoimmune disease', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Daniel Jackson', patient_dob: '1980-12-20', accession: 'ACC-90797', doctor_name: 'Dr. James Lee', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-08', tests: ['Cortisol AM', 'ACTH'], diagnosis: 'E27.1 - Adrenal insufficiency', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
    ],
  },
  '4': {
    invoice_number: 'INV-2024-0044',
    payment_status: 'paid',
    created_at: '2024-03-01T11:00:00Z',
    batch_number: 'BATCH-0044',
    lab_name: 'Sunrise Diagnostics',
    lab_email: 'billing@sunrisedx.com',
    total_amount: 100.00,
    line_items: [
      { patient_name: 'Angela White', patient_dob: '1991-09-14', accession: 'ACC-90770', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-02-29', tests: ['CBC', 'Ferritin'], diagnosis: 'D50.9 - Iron deficiency anemia', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 5.00 },
      { patient_name: 'George Thompson', patient_dob: '1958-06-07', accession: 'ACC-90771', doctor_name: 'Dr. James Lee', phlebotomist_name: 'Maria Garcia', collection_date: '2024-02-29', tests: ['BNP', 'CMP'], diagnosis: 'I50.9 - Heart failure', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Betty Garcia', patient_dob: '1983-03-22', accession: 'ACC-90772', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-03-01', tests: ['Prolactin', 'TSH'], diagnosis: 'E22.1 - Hyperprolactinemia', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Edward Martinez', patient_dob: '1976-11-18', accession: 'ACC-90773', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-03-01', tests: ['Uric Acid', 'CMP'], diagnosis: 'M10.9 - Gout', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
    ],
  },
  '5': {
    invoice_number: 'INV-2024-0043',
    payment_status: 'paid',
    created_at: '2024-02-25T08:00:00Z',
    batch_number: 'BATCH-0043',
    lab_name: 'Sunrise Diagnostics',
    lab_email: 'billing@sunrisedx.com',
    total_amount: 150.00,
    line_items: [
      { patient_name: 'Dorothy Lee', patient_dob: '1969-02-14', accession: 'ACC-90750', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-02-24', tests: ['Hemoglobin', 'Reticulocyte Count'], diagnosis: 'D64.9 - Anemia', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 5.00 },
      { patient_name: 'Frank Wilson', patient_dob: '1952-07-30', accession: 'ACC-90751', doctor_name: 'Dr. James Lee', phlebotomist_name: 'Maria Garcia', collection_date: '2024-02-24', tests: ['Troponin', 'CK-MB', 'BNP'], diagnosis: 'I25.10 - CAD', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Helen Brown', patient_dob: '1994-10-05', accession: 'ACC-90752', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-02-24', tests: ['Estradiol', 'FSH', 'LH'], diagnosis: 'N91.2 - Amenorrhea', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Jack Davis', patient_dob: '1981-05-19', accession: 'ACC-90753', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-02-25', tests: ['Hepatitis Panel', 'Liver Function'], diagnosis: 'B18.2 - Chronic Hep C', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Irene Nguyen', patient_dob: '1986-08-11', accession: 'ACC-90754', doctor_name: 'Dr. James Lee', phlebotomist_name: 'James Wilson', collection_date: '2024-02-25', tests: ['Vitamin D', 'Calcium', 'PTH'], diagnosis: 'E55.9 - Vitamin D deficiency', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Kevin Moore', patient_dob: '1973-12-28', accession: 'ACC-90755', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-02-25', tests: ['PSA', 'Testosterone'], diagnosis: 'Z12.5 - Prostate screening', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
    ],
  },
  '6': {
    invoice_number: 'INV-2024-0042',
    payment_status: 'paid',
    created_at: '2024-02-18T16:00:00Z',
    batch_number: 'BATCH-0042',
    lab_name: 'Sunrise Diagnostics',
    lab_email: 'billing@sunrisedx.com',
    total_amount: 50.00,
    line_items: [
      { patient_name: 'Laura Adams', patient_dob: '1997-04-09', accession: 'ACC-90730', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-02-17', tests: ['CBC', 'CMP'], diagnosis: 'Z00.00 - General examination', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 5.00 },
      { patient_name: 'Mark Evans', patient_dob: '1963-11-23', accession: 'ACC-90731', doctor_name: 'Dr. James Lee', phlebotomist_name: 'Maria Garcia', collection_date: '2024-02-18', tests: ['Lipid Panel', 'HbA1c'], diagnosis: 'E78.5 - Hyperlipidemia', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
    ],
  },
};

const MOCK_CARDS = [
  { id: 'pm_1', brand: 'visa', last4: '4242', exp_month: 12, exp_year: 2026 },
  { id: 'pm_2', brand: 'mastercard', last4: '8888', exp_month: 6, exp_year: 2025 },
];

export default function InvoiceDetailPreview() {
  const { mockId } = useParams();
  const navigate = useNavigate();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [savedCards, setSavedCards] = useState(MOCK_CARDS);
  const [selectedCard, setSelectedCard] = useState(MOCK_CARDS[0]?.id || '');
  const [paymentTab, setPaymentTab] = useState('saved'); // 'saved' or 'new'
  const { isOpen: isPaymentOpen, onOpen: onPaymentOpen, onClose: onPaymentClose } = useDisclosure();
  const { isOpen: isAddCardOpen, onOpen: onAddCardOpen, onClose: onAddCardClose } = useDisclosure();
  const toast = useToast();

  const invoice = MOCK_INVOICES[mockId] || MOCK_INVOICES['1'];
  const allPaid = paymentSuccess || invoice.payment_status === 'paid';

  const handleMockPay = () => {
    onPaymentClose();
    setPaymentSuccess(true);
    toast({ title: 'Payment successful!', description: `Paid ${formatCurrency(invoice.total_amount)}`, status: 'success', duration: 3000 });
  };

  const handleMockAddCard = () => {
    const newCard = { id: `pm_${Date.now()}`, brand: 'amex', last4: '1234', exp_month: 3, exp_year: 2027 };
    setSavedCards(prev => [...prev, newCard]);
    setSelectedCard(newCard.id);
    onAddCardClose();
    toast({ title: 'Card saved!', description: 'Your card has been saved on file.', status: 'success', duration: 3000 });
  };

  return (
    <Box minH="100vh" bg="gray.50" py={{ base: 4, md: 8 }}>
      {/* Demo banner */}
      <Box bg="purple.600" color="white" py={2} textAlign="center" position="sticky" top={0} zIndex={10}>
        <Text fontSize="sm" fontWeight="600">PREVIEW MODE — This is a demo with mock data</Text>
      </Box>

      <Container maxW="800px" px={{ base: 3, md: 6 }} mt={4}>
        {/* Back button */}
        <Button
          size="sm" variant="ghost" leftIcon={<FaArrowLeft />}
          mb={4} onClick={() => navigate('/lab-invoices-preview')}
          color="gray.500"
        >
          Back to Invoices
        </Button>

        {/* Payment Success Banner */}
        {paymentSuccess && (
          <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="lg" p={4} mb={6}>
            <HStack spacing={2}>
              <Icon as={FaCheckCircle} color="green.500" />
              <Box>
                <Text fontWeight="bold" color="green.700">Payment received!</Text>
                <Text fontSize="sm" color="green.600">Thank you for your payment. A confirmation has been sent.</Text>
              </Box>
            </HStack>
          </Box>
        )}

        {/* Main Invoice Card */}
        <Box bg="white" borderRadius="2xl" boxShadow="xl" overflow="hidden" border="1px solid" borderColor="gray.100">

          {/* Header */}
          <Box bgGradient="linear(to-r, #276749, #38a169)" color="white" px={{ base: 6, md: 10 }} py={8} position="relative" overflow="hidden">
            <Box position="absolute" top={0} right={0} bottom={0} left={0} opacity={0.05}
              bgImage="radial-gradient(circle at 25% 25%, white 1px, transparent 1px)" bgSize="20px 20px" />
            <Flex justify="space-between" align="flex-start" position="relative">
              <VStack align="start" spacing={2}>
                <Text fontSize="lg" fontWeight="800" letterSpacing="0.5px">AtHomeBloodDraw.com</Text>
                <Heading size="xl" fontWeight="800" letterSpacing="-0.5px">INVOICE</Heading>
                <Text fontSize="sm" opacity={0.7}>Mobile Phlebotomy Services</Text>
              </VStack>
              <VStack align="end" spacing={2} mt={1}>
                <Text fontSize="xs" opacity={0.6} textTransform="uppercase" letterSpacing="wider">Invoice No.</Text>
                <Text fontSize="2xl" fontWeight="800" letterSpacing="1px">{invoice.invoice_number}</Text>
                <Text fontSize="xs" opacity={0.6}>Issued {formatDate(invoice.created_at)}</Text>
              </VStack>
            </Flex>
          </Box>

          {/* Status Bar */}
          <Box
            bg={allPaid ? 'green.50' : 'orange.50'}
            px={{ base: 6, md: 10 }} py={3}
            borderBottom="1px solid" borderColor={allPaid ? 'green.100' : 'orange.100'}
          >
            <Flex justify="space-between" align="center">
              <HStack spacing={2}>
                <Icon as={allPaid ? FaCheckCircle : FaCreditCard} color={allPaid ? 'green.500' : 'orange.500'} boxSize={4} />
                <Text fontSize="sm" fontWeight="bold" color={allPaid ? 'green.700' : 'orange.700'}>
                  {allPaid ? 'Payment Received' : 'Payment Required'}
                </Text>
              </HStack>
              <Text fontSize="xl" fontWeight="800" color={allPaid ? 'green.700' : 'orange.700'}>
                {formatCurrency(invoice.total_amount)}
              </Text>
            </Flex>
          </Box>

          {/* Bill To Section */}
          <Box px={{ base: 6, md: 10 }} py={6}>
            <SimpleGrid columns={2} spacing={4}>
              <Box>
                <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="bold" letterSpacing="wider" mb={1}>Bill To</Text>
                <Text fontSize="lg" fontWeight="700" color="gray.800">{invoice.lab_name}</Text>
                <Text fontSize="sm" color="gray.500" mt={0.5}>{invoice.lab_email}</Text>
              </Box>
              <Box textAlign="right">
                <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="bold" letterSpacing="wider" mb={1}>Status</Text>
                <Badge
                  colorScheme={allPaid ? 'green' : 'red'}
                  fontSize="sm" px={4} py={1.5} borderRadius="full" fontWeight="700"
                >
                  {allPaid ? 'PAID' : 'UNPAID'}
                </Badge>
              </Box>
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.100" />

          {/* Line Items */}
          <Box px={{ base: 6, md: 10 }} py={5}>
            {/* Column Headers */}
            <Grid
              templateColumns={{ base: '1fr auto', md: '1fr 140px 100px' }}
              gap={4} pb={3} mb={4}
              borderBottom="2px solid" borderColor="gray.200"
            >
              <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="wider">Service Details</Text>
              <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="wider" display={{ base: 'none', md: 'block' }}>Collection Date</Text>
              <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="wider" textAlign="right">Amount</Text>
            </Grid>

            <VStack spacing={0} align="stretch" divider={<Divider borderColor="gray.100" />}>
              {invoice.line_items.map((item, i) => {
                const itemTotal = (item.draw_fee || 0) + (item.admin_fee || 0) + (item.ship_fee || 0);
                return (
                  <Grid
                    key={i}
                    templateColumns={{ base: '1fr auto', md: '1fr 140px 100px' }}
                    gap={4} py={4}
                    _hover={{ bg: 'gray.50' }}
                    mx={-3} px={3} borderRadius="lg"
                    transition="background 0.15s"
                  >
                    <GridItem>
                      <HStack spacing={3} align="flex-start">
                        <Box bg="green.50" borderRadius="lg" p={2} mt={0.5} flexShrink={0}>
                          <Icon as={FaVial} color="green.400" boxSize={3.5} />
                        </Box>
                        <Box flex={1}>
                          <Text fontSize="sm" fontWeight="700" color="gray.800">{item.patient_name}</Text>
                          <HStack spacing={3} mt={1.5} flexWrap="wrap">
                            {item.patient_dob && (
                              <HStack spacing={1}>
                                <Text fontSize="xs" color="gray.400">DOB:</Text>
                                <Text fontSize="xs" color="gray.600" fontWeight="500">{formatDate(item.patient_dob)}</Text>
                              </HStack>
                            )}
                            {item.accession && (
                              <HStack spacing={1}>
                                <Text fontSize="xs" color="gray.400">Acc#:</Text>
                                <Text fontSize="xs" color="gray.600" fontWeight="600" fontFamily="mono">{item.accession}</Text>
                              </HStack>
                            )}
                          </HStack>
                          {(item.doctor_name || item.phlebotomist_name) && (
                            <HStack spacing={4} mt={1.5} flexWrap="wrap">
                              {item.doctor_name && (
                                <HStack spacing={1}>
                                  <Icon as={FaStethoscope} color="gray.300" boxSize={2.5} />
                                  <Text fontSize="xs" color="gray.500">{item.doctor_name}</Text>
                                </HStack>
                              )}
                              {item.phlebotomist_name && (
                                <HStack spacing={1}>
                                  <Icon as={FaSyringe} color="gray.300" boxSize={2.5} />
                                  <Text fontSize="xs" color="gray.500">{item.phlebotomist_name}</Text>
                                </HStack>
                              )}
                            </HStack>
                          )}
                          {item.tests && item.tests.length > 0 && (
                            <HStack spacing={1.5} mt={2} flexWrap="wrap">
                              {item.tests.map((test, ti) => (
                                <Badge
                                  key={ti} bg="green.50" color="green.600"
                                  fontSize="10px" fontWeight="600" px={2} py={0.5}
                                  borderRadius="full" border="1px solid" borderColor="green.100"
                                >
                                  {test}
                                </Badge>
                              ))}
                            </HStack>
                          )}
                          {item.diagnosis && (
                            <HStack spacing={1} mt={1}>
                              <Text fontSize="xs" color="gray.400" fontWeight="600">ICD:</Text>
                              <Text fontSize="xs" color="gray.500">{item.diagnosis}</Text>
                            </HStack>
                          )}
                        </Box>
                      </HStack>
                    </GridItem>
                    <GridItem display={{ base: 'none', md: 'flex' }} alignItems="center">
                      <Text fontSize="sm" color="gray.600">{formatDate(item.collection_date)}</Text>
                    </GridItem>
                    <GridItem display="flex" alignItems="center" justifyContent="flex-end">
                      <VStack spacing={0} align="end">
                        <Text fontSize="sm" fontWeight="700" color="gray.800">{formatCurrency(itemTotal)}</Text>
                        <Text fontSize="10px" color="gray.400">
                          Draw {formatCurrency(item.draw_fee)} + Admin {formatCurrency(item.admin_fee)}
                          {item.ship_fee > 0 ? ` + Ship ${formatCurrency(item.ship_fee)}` : ''}
                        </Text>
                      </VStack>
                    </GridItem>
                  </Grid>
                );
              })}
            </VStack>
          </Box>

          {/* Summary Section */}
          <Box bg="gray.50" px={{ base: 6, md: 10 }} py={5} borderTop="2px solid" borderColor="gray.200">
            <Flex justify="space-between" align="center">
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="bold">Total Due</Text>
                <Text fontSize="sm" color="gray.500">{invoice.line_items.length} draws</Text>
              </VStack>
              <Text fontSize="2xl" fontWeight="800" color={allPaid ? 'green.600' : 'gray.800'}>
                {formatCurrency(invoice.total_amount)}
              </Text>
            </Flex>
          </Box>

          {/* Fee Breakdown */}
          <Box px={{ base: 6, md: 10 }} py={4} borderTop="1px solid" borderColor="gray.100">
            <SimpleGrid columns={3} spacing={4}>
              <Box>
                <Text fontSize="xs" color="gray.400" fontWeight="bold">Draw Fees</Text>
                <Text fontSize="sm" fontWeight="600" color="gray.700">
                  {formatCurrency(invoice.line_items.reduce((s, i) => s + (i.draw_fee || 0), 0))}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.400" fontWeight="bold">Admin Fees</Text>
                <Text fontSize="sm" fontWeight="600" color="gray.700">
                  {formatCurrency(invoice.line_items.reduce((s, i) => s + (i.admin_fee || 0), 0))}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.400" fontWeight="bold">Shipping</Text>
                <Text fontSize="sm" fontWeight="600" color="gray.700">
                  {formatCurrency(invoice.line_items.reduce((s, i) => s + (i.ship_fee || 0), 0))}
                </Text>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Pay Button */}
          {!allPaid && (
            <Box px={{ base: 6, md: 10 }} py={6} borderTop="1px solid" borderColor="gray.100">
              <Button
                bgGradient="linear(to-r, #276749, #38a169)"
                color="white" size="lg" width="full"
                leftIcon={<Icon as={FaCreditCard} />}
                fontSize="md" py={7} borderRadius="xl"
                _hover={{ bgGradient: 'linear(to-r, #38a169, #48bb78)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
                _active={{ transform: 'translateY(0)' }}
                transition="all 0.2s" boxShadow="md"
                onClick={onPaymentOpen}
              >
                Pay {formatCurrency(invoice.total_amount)}
              </Button>
              <HStack spacing={1.5} justify="center" mt={3}>
                <Icon as={FaLock} color="gray.400" boxSize={3} />
                <Text fontSize="xs" color="gray.400">Secure payment powered by Stripe</Text>
              </HStack>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Flex justify="space-between" align="center" mt={6} flexWrap="wrap" gap={2}>
          <Text fontSize="xs" color="gray.500" fontWeight="600">AtHomeBloodDraw.com</Text>
          <Text fontSize="xs" color="gray.400">Questions? Contact support@athomeblooddraw.com</Text>
        </Flex>

        {/* Pay Modal */}
        <Modal isOpen={isPaymentOpen} onClose={onPaymentClose} isCentered size="md">
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
          <ModalContent borderRadius="2xl" mx={4} overflow="hidden">
            <Box bgGradient="linear(to-r, #276749, #38a169)" px={6} py={4}>
              <Flex justify="space-between" align="center">
                <VStack align="start" spacing={0}>
                  <Text color="white" fontSize="lg" fontWeight="700">Payment</Text>
                  <Text color="whiteAlpha.800" fontSize="sm">{formatCurrency(invoice.total_amount)} — {invoice.invoice_number}</Text>
                </VStack>
                <Box as="button" onClick={onPaymentClose} color="whiteAlpha.800" _hover={{ color: 'white' }} p={1}>
                  <Icon as={FaTimes} boxSize={4} />
                </Box>
              </Flex>
            </Box>
            <ModalBody px={6} py={5}>
              <VStack spacing={4} align="stretch">
                {/* Tabs: Saved Card / New Card */}
                <HStack spacing={0} bg="gray.100" borderRadius="lg" p={1}>
                  <Button
                    flex={1} size="sm" borderRadius="md"
                    bg={paymentTab === 'saved' ? 'white' : 'transparent'}
                    color={paymentTab === 'saved' ? 'green.600' : 'gray.500'}
                    boxShadow={paymentTab === 'saved' ? 'sm' : 'none'}
                    fontWeight="600"
                    onClick={() => setPaymentTab('saved')}
                    leftIcon={<FaRegCreditCard />}
                    _hover={{ bg: paymentTab === 'saved' ? 'white' : 'gray.50' }}
                  >
                    Card on File
                  </Button>
                  <Button
                    flex={1} size="sm" borderRadius="md"
                    bg={paymentTab === 'new' ? 'white' : 'transparent'}
                    color={paymentTab === 'new' ? 'green.600' : 'gray.500'}
                    boxShadow={paymentTab === 'new' ? 'sm' : 'none'}
                    fontWeight="600"
                    onClick={() => setPaymentTab('new')}
                    leftIcon={<FaCreditCard />}
                    _hover={{ bg: paymentTab === 'new' ? 'white' : 'gray.50' }}
                  >
                    New Card
                  </Button>
                </HStack>

                {paymentTab === 'saved' ? (
                  <VStack spacing={4} align="stretch">
                    {savedCards.length > 0 ? (
                      <>
                        <Select
                          value={selectedCard}
                          onChange={(e) => setSelectedCard(e.target.value)}
                          size="md" borderRadius="lg"
                          icon={<FaRegCreditCard />}
                        >
                          {savedCards.map(card => (
                            <option key={card.id} value={card.id}>
                              {card.brand.charAt(0).toUpperCase() + card.brand.slice(1)} •••• {card.last4} — Exp {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                            </option>
                          ))}
                        </Select>
                        <Button
                          bgGradient="linear(to-r, #276749, #38a169)" color="white" size="lg" width="full"
                          leftIcon={<Icon as={FaCreditCard} />}
                          fontSize="md" py={7} borderRadius="xl"
                          _hover={{ bgGradient: 'linear(to-r, #38a169, #48bb78)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
                          _active={{ transform: 'translateY(0)' }}
                          transition="all 0.2s" boxShadow="md"
                          onClick={handleMockPay}
                          isDisabled={!selectedCard}
                        >
                          Charge {formatCurrency(invoice.total_amount)}
                        </Button>
                      </>
                    ) : (
                      <Box bg="gray.50" borderRadius="lg" p={4} textAlign="center">
                        <Text fontSize="sm" color="gray.500" mb={3}>No cards on file yet.</Text>
                      </Box>
                    )}
                    <Divider />
                    <Button
                      size="sm" variant="outline" colorScheme="green"
                      leftIcon={<FaPlus />}
                      onClick={() => { onPaymentClose(); onAddCardOpen(); }}
                    >
                      Add New Card on File
                    </Button>
                  </VStack>
                ) : (
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Card Number</Text>
                      <Box as="input" w="full" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" fontSize="sm"
                        placeholder="4242 4242 4242 4242" _focus={{ borderColor: 'green.400', outline: 'none', boxShadow: '0 0 0 1px #38a169' }} />
                    </Box>
                    <HStack spacing={3}>
                      <Box flex={1}>
                        <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Expiry</Text>
                        <Box as="input" w="full" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" fontSize="sm"
                          placeholder="MM / YY" _focus={{ borderColor: 'green.400', outline: 'none', boxShadow: '0 0 0 1px #38a169' }} />
                      </Box>
                      <Box flex={1}>
                        <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>CVC</Text>
                        <Box as="input" w="full" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" fontSize="sm"
                          placeholder="123" _focus={{ borderColor: 'green.400', outline: 'none', boxShadow: '0 0 0 1px #38a169' }} />
                      </Box>
                    </HStack>
                    <Button
                      bgGradient="linear(to-r, #276749, #38a169)" color="white" size="lg" width="full"
                      leftIcon={<Icon as={FaCreditCard} />}
                      fontSize="md" py={7} borderRadius="xl"
                      _hover={{ bgGradient: 'linear(to-r, #38a169, #48bb78)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
                      _active={{ transform: 'translateY(0)' }}
                      transition="all 0.2s" boxShadow="md"
                      onClick={handleMockPay}
                    >
                      Pay {formatCurrency(invoice.total_amount)}
                    </Button>
                  </VStack>
                )}

                <HStack spacing={1.5} justify="center" pb={1}>
                  <Icon as={FaLock} color="gray.400" boxSize={3} />
                  <Text fontSize="xs" color="gray.400">Secure payment powered by Stripe</Text>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Add Card Modal */}
        <Modal isOpen={isAddCardOpen} onClose={onAddCardClose} isCentered size="md">
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
          <ModalContent borderRadius="2xl" mx={4} overflow="hidden">
            <Box bgGradient="linear(to-r, #276749, #38a169)" px={6} py={4}>
              <Flex justify="space-between" align="center">
                <VStack align="start" spacing={0}>
                  <Text color="white" fontSize="lg" fontWeight="700">Add Card on File</Text>
                  <Text color="whiteAlpha.800" fontSize="sm">Save for future payments</Text>
                </VStack>
                <Box as="button" onClick={onAddCardClose} color="whiteAlpha.800" _hover={{ color: 'white' }} p={1}>
                  <Icon as={FaTimes} boxSize={4} />
                </Box>
              </Flex>
            </Box>
            <ModalBody px={6} py={5}>
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Card Number</Text>
                  <Box as="input" w="full" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" fontSize="sm"
                    placeholder="4242 4242 4242 4242" _focus={{ borderColor: 'green.400', outline: 'none', boxShadow: '0 0 0 1px #38a169' }} />
                </Box>
                <HStack spacing={3}>
                  <Box flex={1}>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Expiry</Text>
                    <Box as="input" w="full" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" fontSize="sm"
                      placeholder="MM / YY" _focus={{ borderColor: 'green.400', outline: 'none', boxShadow: '0 0 0 1px #38a169' }} />
                  </Box>
                  <Box flex={1}>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>CVC</Text>
                    <Box as="input" w="full" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" fontSize="sm"
                      placeholder="123" _focus={{ borderColor: 'green.400', outline: 'none', boxShadow: '0 0 0 1px #38a169' }} />
                  </Box>
                </HStack>
                <Button
                  bgGradient="linear(to-r, #276749, #38a169)" color="white" size="lg" width="full"
                  leftIcon={<Icon as={FaPlus} />}
                  fontSize="md" py={7} borderRadius="xl"
                  _hover={{ bgGradient: 'linear(to-r, #38a169, #48bb78)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
                  _active={{ transform: 'translateY(0)' }}
                  transition="all 0.2s" boxShadow="md"
                  onClick={handleMockAddCard}
                >
                  Save Card
                </Button>
                <HStack spacing={1.5} justify="center" pb={1}>
                  <Icon as={FaLock} color="gray.400" boxSize={3} />
                  <Text fontSize="xs" color="gray.400">Your card is securely saved via Stripe</Text>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

      </Container>
    </Box>
  );
}
